import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/api';
import { statsAPI, playersAPI, awardsAPI } from '../api/api';
import PlayerAvatar from '../components/PlayerAvatar';
import BadgeCard, { BADGE_TIER_RANK } from '../components/BadgeCard';

const AWARD_DISPLAY = {
  mvp:             { emoji: '🏆', label: 'MVP' },
  dpoy:            { emoji: '🛡️', label: 'DPOY' },
  gmoy:            { emoji: '🧠', label: 'GM of the Year' },
  mip:             { emoji: '📈', label: 'Most Improved' },
  roty:            { emoji: '🌟', label: 'Rookie of the Year' },
  scoring_leader:  { emoji: '🏀', label: 'Scoring Champion' },
  rebounds_leader: { emoji: '💪', label: 'Rebounds Leader' },
  assists_leader:  { emoji: '🎯', label: 'Assists Leader' },
  blocks_leader:   { emoji: '🚫', label: 'Blocks Leader' },
  steals_leader:   { emoji: '⚡', label: 'Steals Leader' },
};

// Priority order for header badge (most prestigious first)
const AWARD_PRIORITY = ['mvp', 'dpoy', 'roty', 'mip', 'scoring_leader', 'rebounds_leader', 'assists_leader', 'blocks_leader', 'steals_leader'];

function getHeaderBadge(awards) {
  if (!awards.length) return null;
  const maxSeasonId = Math.max(...awards.map(a => a.seasonId));
  const latest = awards.filter(a => a.seasonId === maxSeasonId);
  for (const cat of AWARD_PRIORITY) {
    const match = latest.find(a => a.category === cat);
    if (match) return match;
  }
  return latest[0];
}

const TABS = [
  { key: 'splits', label: 'Season Stats' },
  { key: 'attributes', label: 'Attributes' },
  { key: 'badges', label: 'Badges' },
  { key: 'gamelog', label: 'Game Log' },
  { key: 'trades', label: 'Trade History' },
  { key: 'awards', label: 'Awards' }
];

// ── 2K Attributes / Badges display helpers (see PLAYER_ATTRIBUTES_SPEC.md) ──
const CATEGORY_ORDER = ['outsideScoring', 'insideScoring', 'athleticism', 'playmaking', 'defense', 'rebounding', 'intangibles'];
const CATEGORY_LABELS = {
  outsideScoring: 'Outside Scoring',
  insideScoring: 'Inside Scoring',
  athleticism: 'Athleticism',
  playmaking: 'Playmaking',
  defense: 'Defense',
  rebounding: 'Rebounding',
  intangibles: 'Intangibles',
};

// 2kratings-style value badge: solid colored square, white number.
// ≥90 emerald, 80–89 green, 70–79 amber, 60–69 orange, <60 red.
const attrBadgeClass = (v) => {
  if (v == null) return 'bg-gray-400 dark:bg-gray-600';
  if (v >= 90) return 'bg-emerald-600';
  if (v >= 80) return 'bg-green-600';
  if (v >= 70) return 'bg-amber-500';
  if (v >= 60) return 'bg-orange-500';
  return 'bg-red-600';
};

// Turn a stored camelCase attribute key back into a readable label
// (threePointShot → "Three Point Shot", shotIQ → "Shot IQ").
const prettyAttr = (key) => {
  const s = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const BADGE_CATEGORY_ORDER = ['Shooting', 'Playmaking', 'Finishing', 'Physical', 'Defense', 'Rebounding'];

const PlayerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Player & page state
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playerAwards, setPlayerAwards] = useState([]);
  const [activeTab, setActiveTab] = useState('splits');

  // Season splits state
  const [seasonSplits, setSeasonSplits] = useState([]);
  const [loadingSplits, setLoadingSplits] = useState(false);
  const [splitType, setSplitType] = useState('regular');

  // Trade history state
  const [tradeHistory, setTradeHistory] = useState([]);
  const [loadingTrades, setLoadingTrades] = useState(false);

  // Game log state
  const [stats, setStats] = useState([]);
  const [averages, setAverages] = useState(null);
  const [loadingGameLog, setLoadingGameLog] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [seasons, setSeasons] = useState([]);

  // Initial load: player, seasons, splits, trades
  useEffect(() => {
    const init = async () => {
      try {
        const [playerRes, seasonsRes, awardsRes] = await Promise.all([
          api.get(`/players/${id}`),
          api.get('/seasons'),
          awardsAPI.getPlayerAwards(id).catch(() => ({ data: [] }))
        ]);
        setPlayer(playerRes.data);
        setSeasons(seasonsRes.data);
        setPlayerAwards(awardsRes.data || []);
        const active = seasonsRes.data.find(s => s.isActive);
        if (active) setSelectedSeason(active.id.toString());
      } catch (err) {
        setError('Player not found');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    init();
    fetchSeasonSplits('regular');
    fetchTradeHistory();
  }, [id]);

  // Refetch splits when splitType toggles
  useEffect(() => {
    fetchSeasonSplits(splitType);
  }, [id, splitType]);

  // Fetch game log when selectedSeason or tab changes
  useEffect(() => {
    if (activeTab === 'gamelog' && selectedSeason) {
      fetchGameLog();
    }
  }, [id, selectedSeason, activeTab]);

  const fetchSeasonSplits = async (type) => {
    try {
      setLoadingSplits(true);
      const res = await statsAPI.getSeasonSplits(id, type);
      setSeasonSplits(res.data);
    } catch (err) {
      console.error('Failed to load season splits:', err);
    } finally {
      setLoadingSplits(false);
    }
  };

  const fetchTradeHistory = async () => {
    try {
      setLoadingTrades(true);
      const res = await playersAPI.getTradeHistory(id);
      setTradeHistory(res.data);
    } catch (err) {
      console.error('Failed to load trade history:', err);
    } finally {
      setLoadingTrades(false);
    }
  };

  const fetchGameLog = async () => {
    try {
      setLoadingGameLog(true);
      const res = await api.get(`/stats/player/${id}?seasonId=${selectedSeason}`);
      setStats(res.data.stats);
      setAverages(res.data.averages);
    } catch (err) {
      console.error('Failed to load game log:', err);
    } finally {
      setLoadingGameLog(false);
    }
  };

  const calculateSeasonHighs = () => {
    if (stats.length === 0) return null;
    return {
      points: Math.max(...stats.map(s => s.points)),
      rebounds: Math.max(...stats.map(s => s.rebounds)),
      assists: Math.max(...stats.map(s => s.assists)),
      steals: Math.max(...stats.map(s => s.steals)),
      blocks: Math.max(...stats.map(s => s.blocks))
    };
  };

  const get2KRatingsUrl = (player) => {
    if (!player) return null;
    const playerName = `${player.firstName}-${player.lastName}`.toLowerCase();
    return `https://www.2kratings.com/${playerName}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl">Loading player details...</div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error || 'Player not found'}
        </div>
        <button onClick={() => navigate(-1)} className="mt-4 text-blue-600 dark:text-blue-400 hover:underline">
          ← Go Back
        </button>
      </div>
    );
  }

  const seasonHighs = calculateSeasonHighs();

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Back Button */}
      <button onClick={() => navigate(-1)} className="mb-4 text-blue-600 dark:text-blue-400 hover:underline flex items-center">
        ← Back
      </button>

      {/* Player Header */}
      <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-6 mb-6">
        <div className="flex gap-6 items-start">
          <div className="flex-shrink-0">
            <PlayerAvatar player={player} size="xlarge" className="ring-4 ring-blue-100" />
          </div>
          <div className="flex-1">
            <h1 className="text-4xl font-bold mb-2">
              {player.firstName} {player.lastName}
            </h1>
            <div className="flex items-center gap-4 text-lg text-gray-600 dark:text-gray-400 flex-wrap">
              <span className="font-semibold">{player.team?.city} {player.team?.name}</span>
              <span>•</span>
              <span>#{player.jerseyNumber}</span>
              <span>•</span>
              <span>{player.position}</span>
              <span>•</span>
              <span>{player.height} • {player.weight} lbs</span>
              {player.badges && (
                <>
                  <span>•</span>
                  <span className="text-yellow-600 font-semibold">{player.badges} Badges</span>
                </>
              )}
            </div>
            {/* Latest award badge — single most prestigious from most recent season */}
            {(() => {
              const badge = getHeaderBadge(playerAwards);
              if (!badge) return null;
              const meta = AWARD_DISPLAY[badge.category] || { emoji: '🏅', label: badge.category };
              return (
                <button
                  onClick={() => setActiveTab('awards')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mt-3
                    bg-yellow-100 dark:bg-yellow-900/30
                    border border-yellow-300 dark:border-yellow-700
                    text-yellow-800 dark:text-yellow-300
                    text-xs font-semibold hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition"
                  title={playerAwards.length > 1 ? `${playerAwards.length} career awards — click to see all` : (badge.notes || '')}
                >
                  {meta.emoji} {meta.label} · {badge.seasonName}
                  {playerAwards.length > 1 && (
                    <span className="ml-1 opacity-60">+{playerAwards.length - 1}</span>
                  )}
                </button>
              );
            })()}

            <div className="mt-4 flex items-center gap-4 flex-wrap">
              {player.overallRating && (
                <span className="bg-blue-600 text-white px-3 py-1 rounded font-bold text-lg">
                  {player.overallRating} OVR
                </span>
              )}
              <Link
                to={`/compare?a=${player.id}`}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition font-semibold text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m4 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                {t('compare.compareCta')}
              </Link>
              {player.overallRating && (
                <a
                  href={get2KRatingsUrl(player)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 transition font-semibold text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  View on 2K Ratings
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex gap-6">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Season Stats Tab ── */}
      {activeTab === 'splits' && (
        <div>
          {/* Regular / Playoffs toggle */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setSplitType('regular')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                splitType === 'regular'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Regular Season
            </button>
            <button
              onClick={() => setSplitType('playoffs')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                splitType === 'playoffs'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Playoffs
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg overflow-hidden">
            {loadingSplits ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading stats...</div>
            ) : seasonSplits.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                No {splitType === 'playoffs' ? 'playoff' : 'regular season'} stats recorded yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold sticky left-0 bg-gray-50 dark:bg-gray-700">Season</th>
                      <th className="px-4 py-3 text-left font-semibold">Team</th>
                      <th className="px-4 py-3 text-center font-semibold">GP</th>
                      <th className="px-4 py-3 text-center font-semibold">MIN</th>
                      <th className="px-4 py-3 text-center font-semibold">PTS</th>
                      <th className="px-4 py-3 text-center font-semibold">REB</th>
                      <th className="px-4 py-3 text-center font-semibold">AST</th>
                      <th className="px-4 py-3 text-center font-semibold">STL</th>
                      <th className="px-4 py-3 text-center font-semibold">BLK</th>
                      <th className="px-4 py-3 text-center font-semibold">TOV</th>
                      <th className="px-4 py-3 text-center font-semibold">FG%</th>
                      <th className="px-4 py-3 text-center font-semibold">3P%</th>
                      <th className="px-4 py-3 text-center font-semibold">FT%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seasonSplits.map((row, i) => (
                      <tr
                        key={`${row.seasonId}-${row.teamId ?? 'total'}`}
                        className={`border-b dark:border-gray-700 ${
                          row.isTotal
                            ? 'font-bold bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                        }`}
                      >
                        <td className="px-4 py-2 whitespace-nowrap sticky left-0 bg-white dark:bg-gray-800">
                          {row.isTotal ? '' : (row.season?.year ?? row.season?.name ?? `Season ${row.seasonId}`)}
                          {row.season?.isActive && !row.isTotal && (
                            <span className="ml-1 text-xs text-blue-500 dark:text-blue-400">(Active)</span>
                          )}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {row.isTotal
                            ? <span className="text-gray-500 dark:text-gray-400 text-xs italic">Total</span>
                            : row.team?.abbreviation ?? row.team?.name ?? '–'}
                        </td>
                        <td className="px-4 py-2 text-center">{row.gp}</td>
                        <td className="px-4 py-2 text-center">{row.mpg}</td>
                        <td className="px-4 py-2 text-center text-blue-600 dark:text-blue-400 font-semibold">{row.ppg}</td>
                        <td className="px-4 py-2 text-center">{row.rpg}</td>
                        <td className="px-4 py-2 text-center">{row.apg}</td>
                        <td className="px-4 py-2 text-center">{row.spg}</td>
                        <td className="px-4 py-2 text-center">{row.bpg}</td>
                        <td className="px-4 py-2 text-center">{row.tpg}</td>
                        <td className="px-4 py-2 text-center">{row.fgPct}</td>
                        <td className="px-4 py-2 text-center">{row.tpPct}</td>
                        <td className="px-4 py-2 text-center">{row.ftPct}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Attributes Tab ── */}
      {activeTab === 'attributes' && (
        <div>
          {!player.attributes ? (
            <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-10 text-center">
              <div className="text-5xl mb-3">📊</div>
              <div className="text-gray-500 dark:text-gray-400 text-lg font-medium">No attribute data yet</div>
              <div className="text-gray-400 dark:text-gray-500 text-sm mt-1">Attributes appear here after a 2K ratings sync.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Category cards — 2kratings-style: rating badge in the header,
                  each attribute a colored value badge + label. */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {CATEGORY_ORDER.filter(k =>
                  player.attributes.categories?.[k] != null ||
                  (player.attributes.individual?.[k] && Object.keys(player.attributes.individual[k]).length)
                ).map(k => {
                  const rating = player.attributes.categories?.[k];
                  const rows = player.attributes.individual?.[k] || {};
                  return (
                    <div key={k} className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-5">
                      {/* Card header */}
                      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
                        {rating != null && (
                          <span className={`inline-flex items-center justify-center min-w-[2.25rem] h-8 px-1.5 rounded-md text-sm font-bold text-white ${attrBadgeClass(rating)}`}>
                            {rating}
                          </span>
                        )}
                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{CATEGORY_LABELS[k]}</h3>
                      </div>
                      {/* Attribute rows */}
                      {Object.keys(rows).length > 0 ? (
                        <div className="space-y-2.5">
                          {Object.entries(rows).map(([ak, av]) => (
                            <div key={ak} className="flex items-center gap-3">
                              <span className={`inline-flex items-center justify-center min-w-[2rem] h-7 px-1.5 rounded-md text-xs font-bold text-white ${attrBadgeClass(av)}`}>
                                {av}
                              </span>
                              <span className="text-sm text-gray-700 dark:text-gray-200">{prettyAttr(ak)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400 dark:text-gray-500 italic">Overall rating only</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer summary */}
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {player.attributes.overall != null && (
                  <>Overall <span className="font-semibold text-gray-700 dark:text-gray-200">{player.attributes.overall}</span> · </>
                )}
                {player.attributes.totalAttributes != null && (
                  <>{player.attributes.totalAttributes.toLocaleString()} Total Attributes · </>
                )}
                Potential <span className="font-semibold text-gray-700 dark:text-gray-200">{player.attributes.potential ?? '—'}</span>
                {player.attributes.syncedAt && (
                  <> · synced {new Date(player.attributes.syncedAt).toLocaleDateString()}</>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Badges Tab ── */}
      {activeTab === 'badges' && (
        <div>
          {!player.badgeList || player.badgeList.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-10 text-center">
              <div className="text-5xl mb-3">🏅</div>
              <div className="text-gray-500 dark:text-gray-400 text-lg font-medium">No badge data yet</div>
              <div className="text-gray-400 dark:text-gray-500 text-sm mt-1">Badges appear here after a 2K ratings sync.</div>
            </div>
          ) : (
            (() => {
              const byCategory = player.badgeList.reduce((acc, b) => {
                const c = b.category || 'Other';
                (acc[c] = acc[c] || []).push(b);
                return acc;
              }, {});
              const orderedCats = [
                ...BADGE_CATEGORY_ORDER.filter(c => byCategory[c]),
                ...Object.keys(byCategory).filter(c => !BADGE_CATEGORY_ORDER.includes(c)),
              ];
              return (
                <div className="space-y-6">
                  {/* Header summary line (from the count columns) */}
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    {player.badgesLegendary ? <>Legendary <b>{player.badgesLegendary}</b> · </> : null}
                    HoF <b>{player.badgesHof ?? 0}</b> · Gold <b>{player.badgesGold ?? 0}</b> · Silver <b>{player.badgesSilver ?? 0}</b> · Bronze <b>{player.badgesBronze ?? 0}</b> · {player.badges ?? player.badgeList.length} total
                  </div>

                  {orderedCats.map(cat => {
                    const badges = [...byCategory[cat]].sort(
                      (a, b) => (BADGE_TIER_RANK[a.tier] ?? 9) - (BADGE_TIER_RANK[b.tier] ?? 9)
                    );
                    return (
                      <div key={cat} className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="font-semibold text-gray-800 dark:text-gray-200">{cat}</h3>
                          <span className="text-xs text-gray-400 dark:text-gray-500">({badges.length})</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {badges.map((b, i) => (
                            <BadgeCard key={`${b.name}-${i}`} name={b.name} tier={b.tier} category={cat} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          )}
        </div>
      )}

      {/* ── Game Log Tab ── */}
      {activeTab === 'gamelog' && (
        <div>
          {/* Season Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Season</label>
            <select
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              {seasons.map(season => (
                <option key={season.id} value={season.id}>
                  {season.year} {season.isActive && '(Active)'}
                </option>
              ))}
            </select>
          </div>

          {loadingGameLog ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading game log...</div>
          ) : stats.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-6 text-center text-gray-500 dark:text-gray-400">
              No games played this season yet.
            </div>
          ) : (
            <>
              {/* Season Averages */}
              {averages && (
                <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-6 mb-6">
                  <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Season Averages</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                    {[
                      { val: averages.games, label: 'GP' },
                      { val: averages.ppg, label: 'PPG' },
                      { val: averages.rpg, label: 'RPG' },
                      { val: averages.apg, label: 'APG' },
                      { val: averages.spg, label: 'SPG' },
                      { val: averages.bpg, label: 'BPG' },
                      { val: `${averages.fgPct}%`, label: 'FG%' }
                    ].map(({ val, label }) => (
                      <div key={label} className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded">
                        <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{val}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Season Highs */}
              {seasonHighs && (
                <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-6 mb-6">
                  <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Season Highs</h2>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[
                      { val: seasonHighs.points, label: 'Points' },
                      { val: seasonHighs.rebounds, label: 'Rebounds' },
                      { val: seasonHighs.assists, label: 'Assists' },
                      { val: seasonHighs.steals, label: 'Steals' },
                      { val: seasonHighs.blocks, label: 'Blocks' }
                    ].map(({ val, label }) => (
                      <div key={label} className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">{val}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Game Log */}
              <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-6">
                <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Game Log</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                      <tr>
                        <th className="px-4 py-2 text-left">Date</th>
                        <th className="px-4 py-2 text-left">Opponent</th>
                        <th className="px-4 py-2 text-center">Result</th>
                        <th className="px-4 py-2 text-center">MIN</th>
                        <th className="px-4 py-2 text-center">PTS</th>
                        <th className="px-4 py-2 text-center">REB</th>
                        <th className="px-4 py-2 text-center">AST</th>
                        <th className="px-4 py-2 text-center">STL</th>
                        <th className="px-4 py-2 text-center">BLK</th>
                        <th className="px-4 py-2 text-center">FG</th>
                        <th className="px-4 py-2 text-center">3PT</th>
                        <th className="px-4 py-2 text-center">FT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.map((stat) => {
                        const game = stat.game;
                        if (!game) return null;
                        const isHome = game.homeTeamId === player.teamId;
                        const opponent = isHome ? game.awayTeam : game.homeTeam;
                        const teamScore = isHome ? game.homeScore : game.awayScore;
                        const oppScore = isHome ? game.awayScore : game.homeScore;
                        const won = teamScore > oppScore;
                        return (
                          <tr key={stat.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            <td className="px-4 py-2">{new Date(game.gameDate).toLocaleDateString()}</td>
                            <td className="px-4 py-2">{isHome ? 'vs' : '@'} {opponent?.name || 'Unknown'}</td>
                            <td className="px-4 py-2 text-center">
                              <span className={`font-semibold ${won ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {won ? 'W' : 'L'} {teamScore}-{oppScore}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-center">{stat.minutes}</td>
                            <td className="px-4 py-2 text-center font-semibold">{stat.points}</td>
                            <td className="px-4 py-2 text-center">{stat.rebounds}</td>
                            <td className="px-4 py-2 text-center">{stat.assists}</td>
                            <td className="px-4 py-2 text-center">{stat.steals}</td>
                            <td className="px-4 py-2 text-center">{stat.blocks}</td>
                            <td className="px-4 py-2 text-center">{stat.fieldGoalsMade}-{stat.fieldGoalsAttempted}</td>
                            <td className="px-4 py-2 text-center">{stat.threePointersMade}-{stat.threePointersAttempted}</td>
                            <td className="px-4 py-2 text-center">{stat.freeThrowsMade}-{stat.freeThrowsAttempted}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Trade History Tab ── */}
      {activeTab === 'trades' && (
        <div>
          {loadingTrades ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading trade history...</div>
          ) : tradeHistory.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-8 text-center">
              <div className="text-gray-500 dark:text-gray-400 text-lg">No trade history</div>
              <div className="text-gray-400 dark:text-gray-500 text-sm mt-1">This player has never been traded.</div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Date</th>
                    <th className="px-4 py-3 text-left font-semibold">From</th>
                    <th className="px-4 py-3 text-center font-semibold">→</th>
                    <th className="px-4 py-3 text-left font-semibold">To</th>
                    <th className="px-4 py-3 text-left font-semibold">Other Players in Trade</th>
                  </tr>
                </thead>
                <tbody>
                  {tradeHistory.map(trade => (
                    <tr key={trade.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-400">
                        {trade.processedAt ? new Date(trade.processedAt).toLocaleDateString() : '–'}
                      </td>
                      <td className="px-4 py-3">
                        {trade.fromTeam ? (
                          <Link to={`/teams/${trade.fromTeam.id}`} className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
                            {trade.fromTeam.city} {trade.fromTeam.name}
                          </Link>
                        ) : '–'}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-400">→</td>
                      <td className="px-4 py-3">
                        {trade.toTeam ? (
                          <Link to={`/teams/${trade.toTeam.id}`} className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
                            {trade.toTeam.city} {trade.toTeam.name}
                          </Link>
                        ) : '–'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {trade.otherPlayers && trade.otherPlayers.length > 0
                          ? trade.otherPlayers.map(p => `${p.firstName} ${p.lastName}`).join(', ')
                          : <span className="italic text-gray-400 dark:text-gray-500">None</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Awards Tab ── */}
      {activeTab === 'awards' && (
        <div>
          {playerAwards.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-10 text-center">
              <div className="text-5xl mb-3">🏅</div>
              <div className="text-gray-500 dark:text-gray-400 text-lg font-medium">No awards yet</div>
              <div className="text-gray-400 dark:text-gray-500 text-sm mt-1">Awards will appear here once announced.</div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Group by season, newest first */}
              {Object.entries(
                playerAwards.reduce((acc, a) => {
                  const key = `${a.seasonId}|||${a.seasonName}`;
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(a);
                  return acc;
                }, {})
              )
                .sort(([a], [b]) => parseInt(b.split('|||')[0]) - parseInt(a.split('|||')[0]))
                .map(([key, awards]) => {
                  const seasonName = key.split('|||')[1];
                  // Sort by priority within season
                  const sorted = [...awards].sort(
                    (a, b) => AWARD_PRIORITY.indexOf(a.category) - AWARD_PRIORITY.indexOf(b.category)
                  );
                  return (
                    <div key={key} className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 bg-gradient-to-r from-nba-blue to-nba-red">
                        <h3 className="text-white font-bold text-base">{seasonName}</h3>
                      </div>
                      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {sorted.map(award => {
                          const meta = AWARD_DISPLAY[award.category] || { emoji: '🏅', label: award.category };
                          return (
                            <div key={award.id}
                              className="flex items-center gap-3 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                              <span className="text-2xl">{meta.emoji}</span>
                              <div>
                                <div className="font-bold text-gray-800 dark:text-gray-100 text-sm">{meta.label}</div>
                                {award.notes && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{award.notes}</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayerDetail;

